import { useState } from "react";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import { Save, Loader2, SlidersHorizontal } from "lucide-react";

const CAT_ORDER = [
  "decision_maker", "career_stage", "media_award", "personal_brand",
  "follower_sweet_spot", "business_legitimacy", "growth_signals",
];
// keys that are keyword lists / nested objects we don't expose as plain number inputs
const SKIP_KEYS = new Set([
  "name", "keywords", "explicit_keywords", "implied_keywords",
  "exclusion_keywords", "freelancer_keywords", "signal_keywords",
  "levels", "points_by_count",
]);

const titleCase = (s) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function NumField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">{label}</span>
      <input type="number" value={value} data-testid={testid}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="mt-1 w-full border border-zinc-300 rounded-lg px-2 py-1.5 text-sm font-mono focus:border-klein focus:outline-none" />
    </label>
  );
}

export function RubricEditor({ rubric, onSaved }) {
  const [r, setR] = useState(() => JSON.parse(JSON.stringify(rubric)));
  const [busy, setBusy] = useState(false);

  const cats = r.categories || {};
  const totalMax = CAT_ORDER.reduce((a, k) => a + (Number(cats[k]?.max) || 0), 0);

  const setCatField = (ck, field, val) =>
    setR((p) => ({ ...p, categories: { ...p.categories, [ck]: { ...p.categories[ck], [field]: val } } }));
  const setLevelPoints = (ck, idx, val) =>
    setR((p) => {
      const levels = p.categories[ck].levels.map((lv, i) => (i === idx ? { ...lv, points: val } : lv));
      return { ...p, categories: { ...p.categories, [ck]: { ...p.categories[ck], levels } } };
    });
  const setPointsByCount = (ck, key, val) =>
    setR((p) => ({
      ...p, categories: {
        ...p.categories,
        [ck]: { ...p.categories[ck], points_by_count: { ...p.categories[ck].points_by_count, [key]: val } },
      },
    }));
  const setBand = (idx, field, val) =>
    setR((p) => ({ ...p, score_bands: p.score_bands.map((b, i) => (i === idx ? { ...b, [field]: val } : b)) }));
  const setFlag = (key, val) => setR((p) => ({ ...p, flags: { ...p.flags, [key]: val } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/icp/settings", { rubric: r });
      toast.success("Rubric saved — every lead re-scored");
      onSaved?.();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border-2 border-black p-5 mt-5 rounded-xl" data-testid="rubric-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h3 className="font-head font-bold flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" /> Scoring rubric (0–100)
        </h3>
        <span data-testid="rubric-total"
          className={`text-xs font-mono font-bold px-2 py-1 rounded-md border ${totalMax === 100 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-300"}`}>
          Category max total: {totalMax}/100
        </span>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Every category name, point weight and threshold below is stored as editable configuration — change it here without touching code. Saving re-scores every lead.
      </p>

      <div className="grid lg:grid-cols-2 gap-4">
        {CAT_ORDER.map((ck) => {
          const cat = cats[ck];
          if (!cat) return null;
          const numKeys = Object.keys(cat).filter((k) => !SKIP_KEYS.has(k) && typeof cat[k] === "number");
          return (
            <div key={ck} className="border border-zinc-200 rounded-xl p-3" data-testid={`rubric-cat-${ck}`}>
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-sm font-bold">{cat.name}</span>
                <span className="text-[10px] font-mono text-zinc-400">{ck}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {numKeys.map((k) => (
                  <NumField key={k} label={titleCase(k)} value={cat[k]}
                    testid={`rubric-${ck}-${k}`}
                    onChange={(v) => setCatField(ck, k, v)} />
                ))}
                {Array.isArray(cat.levels) && cat.levels.map((lv, i) => (
                  <NumField key={lv.key} label={`${lv.label || lv.key} pts`} value={lv.points}
                    testid={`rubric-${ck}-level-${lv.key}`}
                    onChange={(v) => setLevelPoints(ck, i, v)} />
                ))}
                {cat.points_by_count && ["3", "2", "1", "0"].map((c) => (
                  <NumField key={c} label={`${c} of 3 → pts`} value={cat.points_by_count[c]}
                    testid={`rubric-${ck}-pbc-${c}`}
                    onChange={(v) => setPointsByCount(ck, c, v)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Score bands */}
      <div className="border border-zinc-200 rounded-xl p-3 mt-4" data-testid="rubric-bands">
        <div className="text-sm font-bold mb-2.5">Final score bands (decision)</div>
        <div className="space-y-2">
          {(r.score_bands || []).map((b, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`rubric-band-${i}`}>
              <input type="number" value={b.min} onChange={(e) => setBand(i, "min", Number(e.target.value))}
                data-testid={`rubric-band-min-${i}`}
                className="w-20 border border-zinc-300 rounded-lg px-2 py-1.5 text-sm font-mono text-center focus:border-klein focus:outline-none" />
              <span className="text-xs text-zinc-400 font-mono">≥</span>
              <input value={b.label} onChange={(e) => setBand(i, "label", e.target.value)}
                data-testid={`rubric-band-label-${i}`}
                className="flex-1 border border-zinc-300 rounded-lg px-2 py-1.5 text-sm focus:border-klein focus:outline-none" />
            </div>
          ))}
        </div>
      </div>

      {/* Flags */}
      <div className="border border-zinc-200 rounded-xl p-3 mt-4" data-testid="rubric-flags">
        <div className="text-sm font-bold mb-2.5">Hard-flag thresholds</div>
        <div className="max-w-xs">
          <NumField label="Follower saturation flag above" value={r.flags?.saturation_follower_threshold}
            testid="rubric-flag-saturation"
            onChange={(v) => setFlag("saturation_follower_threshold", v)} />
        </div>
      </div>

      <button data-testid="save-rubric" onClick={save} disabled={busy}
        className="mt-4 flex items-center gap-2 bg-klein hover:bg-klein-hover text-white px-5 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save rubric & re-score
      </button>
    </div>
  );
}
